import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api, rm, errMsg, useSeo } from "../../lib/api";
import { useAuth, useCart, useLang } from "../../lib/ctx";
import { MY_STATES } from "../../lib/i18n";

const F = ({ label, ...p }) => (
  <label className="block text-xs uppercase tracking-widest text-zinc-400">{label}<input {...p} className="field mt-1.5 normal-case tracking-normal" /></label>
);

export default function Checkout() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { items, games, subtotal } = useCart();
  useSeo("Checkout");
  const [f, setF] = useState({ email: "", name: "", phone: "", line1: "", line2: "", city: "", state: "Selangor", postcode: "", consent: false });
  const [coupon, setCoupon] = useState("");
  const [disc, setDisc] = useState({ code: null, discount: 0 });
  const [ship, setShip] = useState(null);
  const [courier, setCourier] = useState("jnt");
  const [pm, setPm] = useState("stripe");
  const [busy, setBusy] = useState(false);
  const [methods, setMethods] = useState([]);
  const physical = items.length > 0;
  useEffect(() => {
    if (user) setF((x) => ({ ...x, email: x.email || user.email, name: x.name || user.name, phone: x.phone || user.phone || "" }));
  }, [user]);
  useEffect(() => {
    api.get("/catalog/meta").then((r) => setMethods(r.data.payment_methods));
  }, []);
  useEffect(() => {
    if (!physical) return setShip(null);
    const t0 = setTimeout(() => {
      api.post("/shipping/quote", { state: f.state, postcode: /^\d{5}$/.test(f.postcode) ? f.postcode : "", items: items.map((i) => ({ variant_id: i.variant_id, qty: i.qty })) }).then((r) => setShip(r.data)).catch(() => setShip(null));
    }, 400);
    return () => clearTimeout(t0);
  }, [f.state, f.postcode, items, physical]);
  const opt = ship?.options?.find((o) => o.code === courier);
  useEffect(() => {
    if (ship && opt && !opt.available) { const a = ship.options.find((o) => o.available); if (a) setCourier(a.code); }
  }, [ship, opt]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setDisc(data);
      toast.success(`Discount applied: ${rm(data.discount)}`);
    } catch (e) { setDisc({ code: null, discount: 0 }); toast.error(errMsg(e)); }
  };
  const total = subtotal - disc.discount + (opt?.fee || 0);
  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        email: f.email, name: f.name, phone: f.phone, marketing_consent: f.consent, coupon: disc.code, courier, payment_method: pm, ref_code: localStorage.getItem("ddv_ref"),
        address: physical ? { line1: f.line1, line2: f.line2, city: f.city, state: f.state, postcode: f.postcode } : null,
        items: items.map((i) => ({ variant_id: i.variant_id, qty: i.qty })), game_items: games.map((g) => ({ game_product_id: g.game_product_id, player_id: g.player_id, server_id: g.server_id || "" })),
        origin_url: window.location.origin,
      });
      localStorage.setItem("ddv_last_order", JSON.stringify({ number: data.order_number, email: f.email }));
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  if (!items.length && !games.length) return <div className="p-20 text-center"><p className="text-zinc-400">{t("emptyCart")}</p><Link to="/shop" className="btn-gold mt-6">{t("continueShopping")}</Link></div>;
  return (
    <form onSubmit={pay} className="mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-8 lg:grid-cols-[1fr_420px] lg:px-12" data-testid="checkout-page">
      <div className="space-y-10">
        <h1 className="font-display text-5xl sm:text-6xl">{t("checkout")}</h1>
        <section className="space-y-4"><p className="eyebrow">{t("contact")}</p>
          {!user && <p className="text-xs text-zinc-500">{t("guestNote")} <Link to="/login" className="text-[#C8A45D]">{t("login")}</Link></p>}
          <F label={t("email")} type="email" required value={f.email} onChange={set("email")} data-testid="checkout-email-input" />
          {user && !user.email && <p className="-mt-2 text-xs text-zinc-500" data-testid="checkout-email-save-note">{lang === "bm" ? "Emel ini akan disimpan ke akaun anda untuk resit dan kemas kini pesanan." : "We'll save this email to your account for receipts and order updates."}</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <F label={t("fullName")} required value={f.name} onChange={set("name")} data-testid="checkout-name-input" />
            <F label={t("phone")} required value={f.phone} onChange={set("phone")} placeholder="+60..." data-testid="checkout-phone-input" />
          </div>
        </section>
        {physical && (
          <section className="space-y-4"><p className="eyebrow">{t("shippingAddress")}</p>
            <F label={t("address1")} required value={f.line1} onChange={set("line1")} data-testid="checkout-address1-input" />
            <F label={t("address2")} value={f.line2} onChange={set("line2")} data-testid="checkout-address2-input" />
            <div className="grid gap-4 sm:grid-cols-3">
              <F label={t("city")} required value={f.city} onChange={set("city")} data-testid="checkout-city-input" />
              <label className="block text-xs uppercase tracking-widest text-zinc-400">{t("state")}
                <select value={f.state} onChange={set("state")} className="field mt-1.5" data-testid="checkout-state-select">{MY_STATES.map((s) => <option key={s}>{s}</option>)}</select></label>
              <F label={t("postcode")} required pattern="\d{5}" value={f.postcode} onChange={set("postcode")} data-testid="checkout-postcode-input" />
            </div>
            <div className="space-y-2" data-testid="courier-options"><p className="eyebrow pt-2">{t("courier")}</p>
              {(ship?.options || []).map((o) => (
                <label key={o.code} className={`flex cursor-pointer items-center justify-between border p-4 text-sm transition-colors ${courier === o.code ? "border-[#C8A45D] bg-[#C8A45D]/5" : "border-white/10"} ${o.available ? "" : "pointer-events-none opacity-40"}`} data-testid={`courier-option-${o.code}`}>
                  <span className="flex items-center gap-3"><input type="radio" name="courier" checked={courier === o.code} onChange={() => setCourier(o.code)} disabled={!o.available} className="accent-[#C8A45D]" />{o.name}</span>
                  <span className="font-mono text-xs">{!o.available ? t("unavailable") : ship.free_shipping ? "FREE" : rm(o.fee)}</span>
                </label>
              ))}
              {ship && <p className="font-mono text-[10px] text-zinc-500" data-testid="courier-mode">{ship.mode === "easyparcel_live" ? "Live rates via EasyParcel" : "Standard rates"} · {ship.weight_kg}kg</p>}
            </div>
          </section>
        )}
        <section className="space-y-3"><p className="eyebrow">{t("payment")}</p>
          {methods.map((m) => (
            <label key={m.code} className={`flex items-center justify-between border p-4 text-sm transition-colors ${m.enabled ? (pm === m.code ? "cursor-pointer border-[#C8A45D] bg-[#C8A45D]/5" : "cursor-pointer border-white/20") : "border-white/10 opacity-50"}`} data-testid={`payment-method-${m.code}`}>
              <span className="flex items-center gap-3"><input type="radio" name="pm" disabled={!m.enabled} checked={pm === m.code} onChange={() => setPm(m.code)} className="accent-[#C8A45D]" data-testid={`payment-radio-${m.code}`} />{m.label}</span><span className="font-mono text-[10px] uppercase text-zinc-400">{m.enabled ? (m.mode === "test" || m.mode === "sandbox" ? "Test mode" : "Enabled") : m.note}</span>
            </label>
          ))}
          <label className="flex items-start gap-2 text-xs text-zinc-400"><input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-0.5 accent-[#C8A45D]" data-testid="checkout-consent-checkbox" />{t("consent")}</label>
        </section>
      </div>
      <aside className="card-dark h-fit space-y-4 p-6 lg:sticky lg:top-36" data-testid="order-summary">
        <p className="eyebrow">{t("orderSummary")}</p>
        {items.map((i) => <div key={i.variant_id} className="flex justify-between text-sm"><span>{i.title} <span className="text-zinc-500">{i.size} × {i.qty}</span></span><span>{rm(i.price * i.qty)}</span></div>)}
        {games.map((g) => <div key={g.key} className="flex justify-between text-sm"><span>{g.title}</span><span>{rm(g.price)}</span></div>)}
        <div className="flex gap-2 border-t border-white/10 pt-4">
          <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder={t("couponCode")} className="field" data-testid="checkout-coupon-input" />
          <button type="button" onClick={applyCoupon} className="btn-ghost px-4" data-testid="checkout-apply-coupon-btn">{t("apply")}</button>
        </div>
        <div className="space-y-2 border-t border-white/10 pt-4 text-sm">
          <div className="flex justify-between"><span className="text-zinc-400">{t("subtotal")}</span><span>{rm(subtotal)}</span></div>
          {disc.discount > 0 && <div className="flex justify-between text-[#C8A45D]"><span>{t("discount")} ({disc.code})</span><span>-{rm(disc.discount)}</span></div>}
          {physical && <div className="flex justify-between"><span className="text-zinc-400">{t("shipping")}</span><span data-testid="checkout-shipping-fee">{opt ? (ship.free_shipping ? "FREE" : rm(opt.fee)) : "-"}</span></div>}
          {opt && <p className="font-mono text-[10px] text-zinc-500">{opt.name} · {ship.zone === "east" ? "East" : "West"} Malaysia</p>}
          <div className="flex justify-between border-t border-white/10 pt-3 text-lg font-semibold"><span>{t("total")}</span><span className="text-[#C8A45D]" data-testid="checkout-total">{rm(total)}</span></div>
        </div>
        <button disabled={busy} className="btn-gold w-full" data-testid="checkout-pay-btn"><Lock className="h-4 w-4" /> {busy ? "…" : t("payNow")}</button>
        <p className="text-center text-[11px] text-zinc-500">{pm === "toyyibpay" ? "Secure toyyibPay checkout (Touch 'n Go eWallet / FPX)." : "Secure Stripe hosted checkout. Card details never touch our servers."}</p>
      </aside>
    </form>
  );
}
